/* eslint-disable ember/no-computed-properties-in-native-classes, ember/classic-decorator-no-classic-methods */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import dayjs from 'dayjs';
import { b64EncodeUnicode } from 'frontend-utils/utils/base64-encode-decode';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import {
  isPlatformDomain,
  PLATFORM_PRODUCTION_DOMAIN,
} from 'frontend-utils/utils/domain-checks';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import Cookies from 'js-cookie';
import {
  BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
  DRAWER_IDS,
  MI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
} from 'lumen/utils/constants/general-constants';
import {
  TRIAL_INDICATION_LOCALSTORAGE,
  TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
  TRIAL_INDICATION_LOCALSTORAGE_KEY,
  TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
} from 'lumen/utils/constants/trial-indication-constants';

type Option = {
  action: string;
  icon?: IconName;
  label: string;
};

export default class MarketController extends Controller {
  @service declare access: Services['access'];

  @service declare ajax: Services['ajax'];
  @service declare drawer: Services['drawer'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];

  active = false;
  currentHotelId = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasStaffAccessIndicator() {
    if (this.isDemoMode) {
      return false;
    }

    return this.access.hasRestrictedFeatures || this.access.hasDeniedFeatures;
  }

  @computed('hotelAndMarketService.hotelOrMarket')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAccountButtonActive() {
    return this.router.currentRouteName === 'market.revpak-exports';
  }

  get isDemoMode(): boolean {
    return Boolean(Cookies.get('demoModev3'));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHappyHolidays() {
    const weekOfYear = dayjs().week();

    return weekOfYear === 51 || weekOfYear === 52;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSettingsButtonActive() {
    return (
      this.router.currentRouteName!.includes('settings') &&
      !this.isAccountButtonActive
    );
  }

  get settingsOptions(): Option[] {
    const options: Option[] = [
      {
        label: this.intl.t('menu.my_account'),
        action: 'redirectToMyAccount',
        icon: 'circle-user',
      },
      {
        label: this.intl.t('menu.help'),
        action: 'openHelpCenter',
        icon: 'circle-question-mark',
      },
    ];

    if (
      this.hotelAndMarketService.market &&
      this.access.hasSpiderRevpakLibrary
    ) {
      options.splice(1, 0, {
        label: this.intl.t('menu.spider.revpak-library'),
        action: 'redirectToSpiderMarketRevpak',
        icon: 'bookmark-filled',
      });
    }

    // Insert refer a friend link at 3rd position, in case of no rebranding
    if (!this.hotelOrMarket.hasRebranding) {
      options.splice(2, 0, {
        label: this.intl.t('menu.refer_a_friend'),
        action: 'redirectReferAFriend',
        icon: 'refer-friend',
      });
    }

    options.push({
      label: this.intl.t('menu.logout'),
      action: 'logout',
      icon: 'logout',
    });

    if (this.access.isStaffUser) {
      if (this.hotelAndMarketService.isMyHotels) {
        options.push({
          label: this.intl.t('menu.admin_user'),
          action: 'redirectToAdminUser',
        });
      }
      options.push({
        label: this.intl.t('menu.admin_market'),
        action: 'redirectToAdminMarket',
      });
    }

    if (this.access.hasFeature('user.app_demo_mode')) {
      if (this.isDemoMode) {
        options.push({
          label: this.intl.t('menu.disable_demo'),
          action: 'disableDemo',
        });
      } else {
        options.push({
          label: this.intl.t('menu.enable_demo'),
          action: 'enableDemo',
        });
      }
    }

    return options;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showEmptyState() {
    const isSettingsPage = this.router.currentRouteName!.includes('.settings.');

    return this.access.isContextHiddenForStaffUsers && !isSettingsPage;
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    this.drawer.registerDrawer(DRAWER_IDS.SMART_INSIGHTS_DRAWER);
  }

  @action disableDemo(): void {
    Cookies.remove('demoModev3');
    window.location.reload();
  }

  @action enableDemo(): void {
    Cookies.set(
      'demoModev3',
      b64EncodeUnicode(
        JSON.stringify({
          active: true,
          map: [],
          market: this.hotelAndMarketService.market?.name,
        }),
      ),
    );
    window.location.reload();
  }

  @action logout(): void {
    // Segment stores personal info in localstorage
    // Remove it before logging out
    this.localStorage.removeItem('ajs_user_traits');
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE);
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(BI_UPSELL_CONTACT_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY);

    // Clear firebase auth data
    indexedDB.deleteDatabase('firebaseLocalStorageDb');

    this.ajax
      .post('/logout/')
      .then(() => {
        window.location.href = '/login';
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }

  @action openHelpCenter(): void {
    this.intercom.redirectToIntercomHelp();
  }

  @action performAction(option: Option): void {
    this.send(option.action);
  }

  @action redirectReferAFriend(): void {
    const extraData = [
      {
        name: 'referrer_first_name',
        value: this.hotelAndMarketService.user?.firstName,
      },
      {
        name: 'referrer_last_name',
        value: this.hotelAndMarketService.user?.lastName,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_property_name',
        value: this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name,
      },
    ];

    // Refer a friend menu item is not visible for resellers, so no extra checks required here
    const baseUrl = `https://www.${DOMAIN_NAME}/lp/refer-a-friend`;

    // form a string containing all necessary URL parameters
    let urlParams = '?';

    extraData.forEach((param, index) => {
      urlParams += `${param.name}=${encodeURIComponent(param.value)}`;

      if (index !== extraData.length - 1) {
        urlParams += '&';
      }
    });

    openUrl(`${baseUrl}${urlParams}`);
  }

  @action redirectToAdminMarket(): void {
    let path = '/admin/';

    if (!this.hotelAndMarketService.isMyHotels) {
      path += `subscriptions/market/${this.hotelAndMarketService.market?.id}/`;
    }

    // Admin only feature, so no further domain checks necessary here
    const url = isPlatformDomain()
      ? `https://${window.location.hostname}${path}`
      : `https://${PLATFORM_PRODUCTION_DOMAIN}${path}`;

    openUrl(url);
  }

  @action redirectToAdminUser(): void {
    let path = '/admin/';

    if (this.hotelAndMarketService.isMyHotels) {
      const marketId = this.hotelAndMarketService.market!.id!.replace('_', '');
      path += `users/user/${marketId}/change/`;
    }

    // Admin only feature, so no further domain checks necessary here
    const url = isPlatformDomain()
      ? `https://${window.location.hostname}${path}`
      : `https://${PLATFORM_PRODUCTION_DOMAIN}${path}`;

    openUrl(url);
  }

  @action redirectToMyAccount(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('market.settings.personal');
  }

  @action redirectToSettings(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('market.settings');
  }

  @action redirectToSpiderMarketRevpak(): void {
    this.router.transitionTo(
      'market.revpak-exports',
      this.hotelAndMarketService.market!.id,
    );
  }

  @action redirectToTripadvisorManagementConsole(): void {
    window.parent.postMessage(
      {
        action: 'redirect-to-management-center',
      },
      '*',
    );
  }

  @action toggleActive(): void {
    this.toggleProperty('active');
  }

  @action toggleSmartInsightsDrawer(): void {
    this.drawer.toggleDrawerState(DRAWER_IDS.SMART_INSIGHTS_DRAWER);
  }
}
